import { create } from 'zustand'
import rubWithdrawApi from '../services/rubWithdrawApi'
import useAppStore from '../../../store/useAppStore'

/**
 * Rub Withdraw Store
 * Общее состояние вывода в рублях для обоих способов (СБП и карта РФ).
 * Экран загружается страницей вывода: от него зависит, какие способы показывать.
 * Пока есть активная заявка (new / approved), экран опрашивается раз в 20 секунд;
 * когда заявка завершилась — обновляется баланс (при отказе кристаллы вернулись).
 */

const POLLING_INTERVAL = 20000 // 20 секунд

// Коды 403, при которых раздел игроку недоступен: убираем способы из списка
const HIDE_METHOD_ERRORS = ['disabled', 'deposit required']

const hideRubMethods = () => {
  const { user, setUser } = useAppStore.getState()
  if (!user || !('rub_withdraw_enabled' in user)) return
  const nextUser = { ...user }
  delete nextUser.rub_withdraw_enabled
  setUser(nextUser)
}

const useRubWithdrawStore = create((set, get) => ({
  // Данные экрана из GET /api/rub-withdraw/ (курс, methods, sbp_banks, reason, active)
  info: null,
  history: [],

  isLoading: false,
  isSubmitting: false,
  error: null,

  _pollingTimer: null,

  /**
   * Применить ответ GET: сохранить данные, управлять опросом, обновить баланс по завершении заявки
   */
  _applyData: (data) => {
    const hadActive = Boolean(get().info?.active)
    set({ info: data, history: data.history || [] })

    if (data.active) {
      if (!get()._pollingTimer) get().startPolling()
    } else {
      get().stopPolling()
      if (hadActive) useAppStore.getState().loadProfile?.()
    }
  },

  _handleForbidden: (error) => {
    if (HIDE_METHOD_ERRORS.includes(error?.message)) hideRubMethods()
  },

  /**
   * Загрузить данные экрана
   */
  loadData: async () => {
    set({ isLoading: true, error: null })
    try {
      const data = await rubWithdrawApi.fetchRubWithdrawData()
      get()._applyData(data)
      set({ isLoading: false })
      return data
    } catch (error) {
      console.error('❌ Ошибка загрузки данных вывода в рублях:', error)
      get()._handleForbidden(error)
      set({ isLoading: false, error: error.message || 'error backend' })
      throw error
    }
  },

  /**
   * Создать заявку
   * При 409 rate changed курс в info заменяется на новый — компонент
   * показывает новую сумму и ждёт явного подтверждения
   * @param {Object} payload — тело POST /api/rub-withdraw/
   * @returns {Promise<Object>} созданная заявка
   */
  submitRubWithdraw: async (payload) => {
    set({ isSubmitting: true, error: null })
    try {
      const result = await rubWithdrawApi.createRubWithdraw(payload)
      set({ isSubmitting: false })

      try {
        await get().loadData()
      } catch {
        // GET не удался — показываем созданную заявку как активную до следующего опроса
        set((state) => ({
          info: state.info ? { ...state.info, active: result } : null,
          history: [result, ...state.history],
        }))
        get().startPolling()
      }

      return result
    } catch (error) {
      console.error('❌ Ошибка создания заявки вывода в рублях:', error)
      set({ isSubmitting: false, error: error.message || 'error backend' })

      if (error.message === 'rate changed' && error.data?.crystals_per_1000_rub_net) {
        set((state) => ({
          info: { ...state.info, crystals_per_1000_rub_net: error.data.crystals_per_1000_rub_net },
        }))
      } else if (['active request', 'method disabled'].includes(error.message)) {
        get().loadData().catch(() => {})
      } else {
        get()._handleForbidden(error)
      }

      throw error
    }
  },

  /**
   * Опрос экрана, пока есть активная заявка
   */
  startPolling: () => {
    get().stopPolling()
    const timer = setInterval(async () => {
      try {
        const data = await rubWithdrawApi.fetchRubWithdrawData()
        get()._applyData(data)
      } catch (error) {
        console.error('❌ Ошибка опроса вывода в рублях:', error)
        get()._handleForbidden(error)
        if (HIDE_METHOD_ERRORS.includes(error?.message)) get().stopPolling()
      }
    }, POLLING_INTERVAL)
    set({ _pollingTimer: timer })
  },

  stopPolling: () => {
    const { _pollingTimer } = get()
    if (_pollingTimer) {
      clearInterval(_pollingTimer)
      set({ _pollingTimer: null })
    }
  },

  reset: () => {
    get().stopPolling()
    set({ info: null, history: [], isLoading: false, isSubmitting: false, error: null })
  },
}))

export default useRubWithdrawStore
